import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ExpenseSettleable,
  ExpenseSettlement,
  ExpenseSettlementCreateCommand,
  ExpenseSettlementJournalPreview,
  ExpenseSettlementOwner,
  ExpenseSettlementSearchCommand,
  ExpenseSettlementUpdate,
} from '../models/expense-settlement/expense-settlement';
import { FundingBankAccount } from '../models/fund-movement/fund-movement';
import { PagedResult } from './bank-account.proxy.service';

/** Expense settlements (Liquidación, #5799). */
@Injectable({ providedIn: 'root' })
export class ExpenseSettlementProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/expense-settlement';

  public search(
    command: ExpenseSettlementSearchCommand,
    pageNumber: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: number,
  ): Observable<ApiResponse<PagedResult<ExpenseSettlement>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<ExpenseSettlement>>>(`${this.baseUrl}/search`, command, { params });
  }

  /** The funds and trips with posted claims still to settle — the treasurer's queue. */
  public getOwners(): Observable<ApiResponse<ExpenseSettlementOwner[]>> {
    return this.httpClient.get<ApiResponse<ExpenseSettlementOwner[]>>(`${this.baseUrl}/owners`);
  }

  public getSettleable(ownerReference: string, ownerCode: number): Observable<ApiResponse<ExpenseSettleable>> {
    const params = new HttpParams().set('ownerReference', ownerReference).set('ownerCode', ownerCode);
    return this.httpClient.get<ApiResponse<ExpenseSettleable>>(`${this.baseUrl}/settleable`, { params });
  }

  public getById(id: string): Observable<ApiResponse<ExpenseSettlement>> {
    return this.httpClient.get<ApiResponse<ExpenseSettlement>>(`${this.baseUrl}/${id}`);
  }

  public getBankAccounts(id: string): Observable<ApiResponse<FundingBankAccount[]>> {
    return this.httpClient.get<ApiResponse<FundingBankAccount[]>>(`${this.baseUrl}/${id}/bank-accounts`);
  }

  public getJournalPreview(id: string): Observable<ApiResponse<ExpenseSettlementJournalPreview>> {
    return this.httpClient.get<ApiResponse<ExpenseSettlementJournalPreview>>(`${this.baseUrl}/${id}/journal-preview`);
  }

  public create(command: ExpenseSettlementCreateCommand): Observable<ApiResponse<ExpenseSettlement>> {
    return this.httpClient.post<ApiResponse<ExpenseSettlement>>(this.baseUrl, command);
  }

  public update(id: string, update: ExpenseSettlementUpdate): Observable<ApiResponse<ExpenseSettlement>> {
    return this.httpClient.put<ApiResponse<ExpenseSettlement>>(`${this.baseUrl}/${id}`, update);
  }

  public post(id: string): Observable<ApiResponse<ExpenseSettlement>> {
    return this.httpClient.post<ApiResponse<ExpenseSettlement>>(`${this.baseUrl}/${id}/post`, {});
  }

  public addAttachment(id: string, file: File): Observable<ApiResponse<ExpenseSettlement>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<ApiResponse<ExpenseSettlement>>(`${this.baseUrl}/${id}/attachment`, formData);
  }

  public delete(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }
}
